import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Box, Button, IconButton, useTheme } from "@mui/material";
import Header from "components/Header";
import { useDispatch, useSelector } from "react-redux";
import MUIDataTable from "mui-datatables";
import moment from "moment";
import "moment/locale/id";
import {
  fetchDeleteAbsensi,
  fetchEditAbsensi,
  fetchListAbsensiHarian,
  fetchOptionsAbsensiHarian,
} from "state/absensiActions";
import { BASEURL } from "api/axiosInstance";
import { Delete, Edit, FilterList, Search } from "@mui/icons-material";
import EditAbsensiModal from "components/Modals/EditAbsensiModal";
import FloatingAlert from "components/FloatingAlert";
import { setMessage, setStatus } from "state";
import CustomFilterModal from "./CustomFilterModal";
import SortableHeader from "components/SortableHeader";
import LokasiCell from "components/Modals/LokasiCell";
import KonfirmasiModal from "components/Modals/KonfirmasiModal";
import PinModal from "components/Modals/PinModal";
import { fetchCurrentPIN } from "state/userActions";
moment.locale("id");

const RekapAbsensiHarian = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [openModalAbsensi, setOpenModalAbsensi] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [openPinDialog, setOpenPinDialog] = useState(false);
  const [pinAction, setPinAction] = useState("");
  const [pendingEditData, setPendingEditData] = useState(null);
  const { status, message } = useSelector((state) => state.global);
  const { list_absensi_harian, pagination_absensi_harian, options_absensi_harian } =
    useSelector((state) => state.absensi);
  const { currentPin } = useSelector((state) => state.user);
  const [appliedFilters, setAppliedFilters] = React.useState({});
  const [openCustomFilter, setOpenCustomFilter] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [page, setPage] = React.useState(0);
  const [headerSort, setHeaderSort] = React.useState({ name: "", direction: "" });
  const searchTextRef = useRef("");

  const columnSort = [
    { value: "id_karyawan", label: "ID Karyawan" },
    { value: "nama_karyawan", label: "Nama Karyawan" },
    { value: "tanggal", label: "Tanggal" },
    { value: "telat_menit", label: "Telat Menit" },
    { value: "status", label: "Status" },
    { value: "cabang", label: "Cabang" },
  ];

  const buildQueryParams = useCallback(() => {
    const p = { page: page + 1, limit: rowsPerPage };
    const f = appliedFilters;
    if (f.search && f.search.trim() !== "") p.search = f.search.trim();
    if (f.dateRange?.tanggalAwal) p.tanggal_awal = f.dateRange.tanggalAwal;
    if (f.dateRange?.tanggalAkhir) p.tanggal_akhir = f.dateRange.tanggalAkhir;
    if (f.filter?.cabang?.length) p.cabang = f.filter.cabang.join(",");
    if (f.filter?.nama_jabatan?.length)
      p.nama_jabatan = f.filter.nama_jabatan.join(",");
    // Sort dari klik header table lebih prioritas daripada sort modal filter
    if (headerSort.name && headerSort.direction) {
      p.sort = `${headerSort.name}:${headerSort.direction.toLowerCase()}`;
    } else if (f.sort?.length) {
      const sortStr = f.sort
        .filter((s) => s.column)
        .map((s) => `${s.column}:${s.order}`)
        .join(",");
      if (sortStr) p.sort = sortStr;
    }
    return p;
  }, [page, rowsPerPage, appliedFilters, headerSort]);

  useEffect(() => {
    dispatch(fetchListAbsensiHarian(buildQueryParams()));
  }, [dispatch, buildQueryParams]);

  useEffect(() => {
    dispatch(fetchOptionsAbsensiHarian());
  }, [dispatch]);

  const handleApplyCustomFilter = (filters) => {
    setPage(0);
    setAppliedFilters(filters);
  };

  const handleSearchClick = () => {
    setPage(0);
    setAppliedFilters((prev) => ({ ...prev, search: searchTextRef.current }));
  };

  const handleClearCustomFilter = () => {
    setPage(0);
    setAppliedFilters({});
  };

  const getActiveFilterCount = useCallback(() => {
    let count = 0;

    if (appliedFilters.search && appliedFilters.search.trim() !== "") {
      count++;
    }

    if (appliedFilters.dateRange) {
      if (
        appliedFilters.dateRange.tanggalAwal ||
        appliedFilters.dateRange.tanggalAkhir
      ) {
        count++;
      }
    }

    if (appliedFilters.filter) {
      if (appliedFilters.filter.cabang?.length > 0) count++;
      if (appliedFilters.filter.nama_jabatan?.length > 0) count++;
    }
    if (
      Array.isArray(appliedFilters.sort) &&
      appliedFilters.sort.some(
        (item) => item.column && item.column.trim() !== "",
      )
    ) {
      count++;
    }

    return count;
  }, [appliedFilters]);

  const handleSelectedData = useCallback(
    (id) => {
      let currentSelectedData = list_absensi_harian.find(
        (item) => item.sesi_id === id,
      );
      setSelectedData(currentSelectedData);
    },
    [list_absensi_harian],
  );

  const handleCloseSnackbar = () => {
    dispatch(setStatus("default"));
    dispatch(setMessage(""));
    dispatch(fetchListAbsensiHarian(buildQueryParams()));
    setSelectedData(null);
  };

  const handleSubmitEditAbsensi = (value) => {
    const reqData = {
      id: selectedData.sesi_id,
      shift_absen: value.shift_absen,
      telat_menit: value.telat_menit,
      mode_absen: value.mode_absen,
      waktu_masuk: value.waktu_masuk,
      waktu_kedua: value.waktu_kedua,
      waktu_keluar: value.waktu_keluar,
    };

    // Menyimpan data absensi yang diedit di state pendingEditData (disubmit setelah validasi PIN)
    setPendingEditData(reqData);
    setOpenModalAbsensi(false);
    dispatch(fetchCurrentPIN());
    setPinAction("edit");
    setOpenPinDialog(true);
  };

  const handleDeleteAbsensi = (sesi_id) => {
    dispatch(fetchDeleteAbsensi(sesi_id));
    setOpenModalDelete(false);
    setSelectedData(null);
  };

  const columnsAbsensi = useMemo(
    () => [
      {
        name: "sesi_id",
        label: "Sesi ID",
        options: {
          display: false,
        },
      },
      {
        name: "status",
        label: "Status",
        options: {
          display: false,
        },
      },
      {
        name: "id_karyawan",
        label: "ID Karyawan",
        options: {
          filter: true,
          sort: true,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
              sorting
            />
          ),
        },
      },
      {
        name: "nama_karyawan",
        label: "Nama Karyawan",
        options: {
          filter: true,
          sort: true,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
              sorting
            />
          ),
        },
      },
      {
        name: "cabang",
        label: "Nama Cabang",
        options: {
          filter: true,
          sort: true,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
              sorting
            />
          ),
        },
      },
      {
        name: "tanggal",
        label: "Tanggal",
        options: {
          filter: true,
          sort: true,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
              sorting
            />
          ),
          customBodyRender: (value) => {
            // Tampilkan tanggal yang sudah diformat di tabel
            return value === "-" || !value ? "-" : value;
          },
        },
      },
      {
        name: "shift_absen",
        label: "Shift Absen",
        options: {
          filter: true,
          sort: true,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
              sorting
            />
          ),
        },
      },
      {
        name: "waktu_masuk",
        label: "Waktu Masuk",
        options: {
          filter: true,
          sort: true,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
              sorting
            />
          ),
          customBodyRender: (value, tableMeta) => {
            const shift_absen = tableMeta.rowData[6];
            let cutoffTime;

            if (!value) {
              return <span style={{ color: "gray" }}>-</span>;
            }

            const actualTime = moment(value, "HH:mm:ss");
            if (!actualTime.isValid())
              return <span style={{ color: "black" }}>{value}</span>;

            let cutoffHour = "08:00:00";
            switch (shift_absen) {
              case "Shift 1 Pagi":
                cutoffHour = "07:00:00";
                break;
              case "Shift 2 Siang":
                cutoffHour = "14:00:00";
                break;
              case "Shift Middle":
                cutoffHour = "11:00:00";
                break;
              case "Shift 4":
                cutoffHour = "08:00:00";
                break;
            }

            cutoffTime = moment(
              `${actualTime.format("YYYY-MM-DD")} ${cutoffHour}`,
              "YYYY-MM-DD HH:mm:ss",
            );

            const diffInMinutes = actualTime.diff(cutoffTime, "minutes", true);
            const isLate = diffInMinutes > 1;

            const style = isLate
              ? { color: "red", fontWeight: "bold" }
              : { color: "black" };

            return <span style={style}>{value}</span>;
          },
        },
      },
      {
        name: "waktu_kedua",
        label: "Waktu Kedua",
        options: {
          filter: true,
          sort: true,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
              sorting
            />
          ),
          customBodyRender: (value, tableMeta) => {
            const waktuMasuk = tableMeta.rowData[tableMeta.columnIndex - 1];
            const shift_absen = tableMeta.rowData[6];

            if (
              !value ||
              value === "-" ||
              value === waktuMasuk ||
              shift_absen !== "Shift 4"
            ) {
              return <span style={{ color: "gray" }}>-</span>;
            }

            const actualTime = moment(value, "HH:mm:ss");
            if (!actualTime.isValid()) {
              return <span style={{ color: "gray" }}>{value}</span>;
            }

            const cutoffTime = moment(
              `${actualTime.format("YYYY-MM-DD")} 15:00:00`,
              "YYYY-MM-DD HH:mm:ss",
            );

            const diffInMinutes = actualTime.diff(cutoffTime, "minutes", true);
            const isLate = diffInMinutes > 1;

            const style = isLate
              ? { color: "red", fontWeight: "bold" }
              : { color: "black" };

            return <span style={style}>{value}</span>;
          },
        },
      },
      {
        name: "waktu_keluar",
        label: "Waktu Keluar",
        options: {
          filter: true,
          sort: true,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
              sorting
            />
          ),
          customBodyRender: (value, tableMeta) => {
            const status = tableMeta.rowData[2];
            if (!value || value === "-" || status === "Absen Pagi") {
              return "-";
            }

            return value;
          },
        },
      },
      {
        name: "telat_menit",
        label: "Telat Menit",
        options: {
          filter: true,
          sort: true,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
              sorting
            />
          ),
          customBodyRender: (value) => {
            const isLate = value > 0;
            const style = isLate
              ? { color: "red", fontWeight: "bold" }
              : { color: "black" };
            return <span style={style}>{Number(value).toFixed(2)}</span>;
          },
        },
      },
      {
        name: "lokasi",
        label: "Lokasi Absen",
        options: {
          filter: true,
          sort: true,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
              sorting
            />
          ),
          customBodyRender: (value) => {
            return <LokasiCell text={value} />;
          },
        },
      },
      {
        name: "jenis",
        label: "Jenis Absen",
        options: {
          filter: true,
          sort: false,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
            />
          ),
        },
      },
      {
        name: "foto",
        label: "Foto Absen",
        options: {
          filter: true,
          sort: false,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
            />
          ),
          customBodyRender: (value) => {
            if (!value) return "-";
            // Pisahkan berdasarkan delimiter " | "
            const items = value.split(" | ");
            return (
              <div>
                {items.map((item, index) => (
                  <div key={index}>
                    <a
                      href={`${BASEURL}/photos/${item}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1976d2", textDecoration: "underline" }}
                    >
                      Foto{index + 1}
                    </a>
                  </div>
                ))}
              </div>
            );
          },
        },
      },
      {
        name: "aksi",
        label: "Aksi",
        options: {
          filter: false,
          sort: false,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
            />
          ),
          customBodyRender: (value, tableMeta) => {
            const id = tableMeta.rowData[0];
            return (
              <>
                <IconButton
                  color="info"
                  onClick={() => {
                    handleSelectedData(id);
                    setOpenModalAbsensi(true);
                  }}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => {
                    handleSelectedData(id);
                    setOpenModalDelete(true);
                  }}
                >
                  <Delete />
                </IconButton>
              </>
            );
          },
        },
      },
    ],
    [handleSelectedData],
  );

  const options = useMemo(
    () => ({
      selectableRows: "none",
      filterType: "dropdown",
      filter: false,
      fixedHeader: false,
      fixedSelectColumn: false,
      search: true,
      textLabels: {
        search: "Cari ID atau nama...",
      },
      onSearchChange: (text) => {
        searchTextRef.current = text || "";
      },
      searchProps: {
        onKeyDown: (e) => {
          if (e.key === "Enter") handleSearchClick();
        },
      },
      viewColumns: false,
      responsive: "standard",
      customToolbar: () => {
        const activeCount = getActiveFilterCount();
        return (
          <Button
            startIcon={<FilterList />}
            onClick={() => setOpenCustomFilter(true)}
            variant={activeCount > 0 ? "contained" : "outlined"}
            size="small"
            color={activeCount > 0 ? "primary" : "inherit"}
            sx={{
              mr: 1,
              ...(activeCount === 0 && {
                color: "text.secondary",
                borderColor: "text.secondary",
              }),
            }}
          >
            Filter {activeCount > 0 && `(${activeCount})`}
          </Button>
        );
      },
      rowsPerPageOptions: [25, 50, 75],
      rowsPerPage: rowsPerPage,
      serverSide: true,
      count: pagination_absensi_harian?.total ?? 0,
      page: page,
      onColumnSortChange: (columnName, direction) => {
        setPage(0);
        setHeaderSort({ name: columnName, direction });
      },
      onChangePage: (currentPage) => setPage(currentPage),
      onChangeRowsPerPage: (numberOfRows) => {
        setRowsPerPage(numberOfRows);
        setPage(0);
      },
    }),
    [page, rowsPerPage, pagination_absensi_harian, getActiveFilterCount],
  );

  return (
    <Box m="1.5rem 2.5rem">
      <Header
        title="Rekap Absensi Harian"
        subtitle="Daftar absensi harian karyawan."
      />
      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <MUIDataTable
          title={""}
          data={list_absensi_harian}
          columns={columnsAbsensi}
          options={options}
        />
        <CustomFilterModal
          open={openCustomFilter}
          onClose={() => setOpenCustomFilter(false)}
          onApplyFilter={handleApplyCustomFilter}
          onClearFilter={handleClearCustomFilter}
          data={list_absensi_harian}
          cabangOptions={options_absensi_harian?.cabang ?? null}
          jabatanOptions={options_absensi_harian?.nama_jabatan ?? null}
          sortableColumns={columnSort}
        />
        <EditAbsensiModal
          open={openModalAbsensi}
          dataEdit={selectedData}
          onClose={() => setOpenModalAbsensi(false)}
          onSubmit={(value) => handleSubmitEditAbsensi(value)}
        />
        <FloatingAlert
          open={message.length > 0}
          onClose={handleCloseSnackbar}
          message={message}
          severity={status === "failed" ? "error" : status}
        />
        <KonfirmasiModal
          titleType={"Hapus"}
          subtitleType={"Menghapus Absensi"}
          dataWillProses={selectedData?.nama_karyawan}
          open={openModalDelete}
          onClose={() => setOpenModalDelete(false)}
          onSubmit={() => {
            setOpenModalDelete(false);
            dispatch(fetchCurrentPIN());
            setPinAction("delete");
            setOpenPinDialog(true);
          }}
        />
        <PinModal
          open={openPinDialog}
          onClose={() => {
            setOpenPinDialog(false);
            setSelectedData(null);
            setPinAction("");
            setPendingEditData(null);
          }}
          title="Verifikasi PIN"
          subtitle={
            pinAction === "delete"
              ? "Masukkan 6 digit PIN untuk menghapus data ini."
              : "Masukkan 6 digit PIN untuk menyimpan perubahan data."
          }
          correctPin={currentPin}
          onSuccess={() => {
            if (pinAction === "delete") {
              handleDeleteAbsensi(selectedData.sesi_id);
            } else if (pinAction === "edit") {
              dispatch(fetchEditAbsensi(pendingEditData));
              setOpenPinDialog(false);
              setPinAction("");
              setPendingEditData(null);
              setSelectedData(null);
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default RekapAbsensiHarian;
